import { motion } from 'framer-motion'
import { useState } from 'react'
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface Scenario {
  id: string
  title: string
  description: string
  probability: number
  timeframe: string
  implications: string[]
  color: string
  icon: React.ComponentType<any>
  penetrationBy2030: number
}

const AIScenarios = () => {
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null)
  const { t } = useLanguage()

  const scenarios: Scenario[] = [
    {
      id: 'acceleration',
      title: t('scenarios.acceleration'),
      description: t('scenarios.accelerationDesc'),
      probability: 35,
      timeframe: '2025-2027',
      implications: [
        'AI penetration reaches 85%+ by 2030',
        'Massive productivity gains across industries',
        'Significant job displacement and economic disruption',
        'Potential AI safety and alignment challenges'
      ],
      color: 'from-red-500 to-orange-500',
      icon: TrendingUp,
      penetrationBy2030: 85
    },
    {
      id: 'controlled',
      title: t('scenarios.controlled'),
      description: t('scenarios.controlledDesc'),
      probability: 45,
      timeframe: '2025-2030',
      implications: [
        'AI penetration reaches 60-70% by 2030',
        'Steady economic growth with managed transitions',
        'Strong AI safety protocols and international cooperation',
        'Gradual adaptation of workforce and society'
      ],
      color: 'from-green-500 to-emerald-500',
      icon: CheckCircle,
      penetrationBy2030: 65
    },
    {
      id: 'slowdown',
      title: t('scenarios.slowdown'),
      description: t('scenarios.slowdownDesc'),
      probability: 20,
      timeframe: '2025-2035',
      implications: [
        'AI penetration reaches only 45-50% by 2030',
        'Slower economic transformation',
        'Reduced AI-related risks but missed opportunities',
        'Potential competitive disadvantage for restrictive regions'
      ],
      color: 'from-blue-500 to-cyan-500',
      icon: TrendingDown,
      penetrationBy2030: 47
    }
  ]

  const getSelectedScenario = () => scenarios.find(s => s.id === selectedScenario)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-ai-600 to-primary-600 p-8 text-white">
        <h3 className="text-2xl font-bold mb-2">{t('scenarios.title')}</h3>
        <p className="text-ai-100">{t('scenarios.subtitle')}</p>
      </div>

      {/* Scenario Selection */}
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {scenarios.map((scenario) => (
            <motion.div
              key={scenario.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                selectedScenario === scenario.id
                  ? 'border-ai-300 bg-ai-50 shadow-lg'
                  : 'border-gray-200 hover:border-ai-200 hover:shadow-md'
              }`}
              onClick={() => setSelectedScenario(selectedScenario === scenario.id ? null : scenario.id)}
            >
              {/* Icon and Title */}
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-r ${scenario.color} rounded-lg flex items-center justify-center`}>
                  <scenario.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">{scenario.title}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{scenario.probability}% {t('scenarios.probability')}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {scenario.timeframe}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-4">{scenario.description}</p>

              {/* Penetration Preview */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{t('scenarios.penetration2030')}:</span>
                <span className="font-bold text-lg">{scenario.penetrationBy2030}%</span>
              </div>

              {/* Selection indicator */}
              {selectedScenario === scenario.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-4 right-4 w-6 h-6 bg-ai-500 rounded-full flex items-center justify-center"
                >
                  <CheckCircle className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Detailed View */}
        {selectedScenario && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-200 pt-8"
          >
            {(() => {
              const scenario = getSelectedScenario()!
              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Implications */}
                  <div>
                    <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
                      Key Implications
                    </h5>
                    <ul className="space-y-3">
                      {scenario.implications.map((implication, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start space-x-3"
                        >
                          <ArrowRight className="w-4 h-4 text-ai-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{implication}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Visualization */}
                  <div>
                    <h5 className="text-lg font-bold text-gray-900 mb-4">{t('scenarios.projectedPenetration')}</h5>
                    <div className="relative">
                      {/* Progress bar */}
                      <div className="w-full bg-gray-200 rounded-full h-8 mb-4">
                        <motion.div
                          className={`h-8 bg-gradient-to-r ${scenario.color} rounded-full flex items-center justify-end pr-4`}
                          initial={{ width: '37.2%' }}
                          animate={{ width: `${scenario.penetrationBy2030}%` }}
                          transition={{ duration: 2, ease: "easeInOut" }}
                        >
                          <span className="text-white font-bold text-sm">
                            {scenario.penetrationBy2030}%
                          </span>
                        </motion.div>
                      </div>

                      {/* Timeline markers */}
                      <div className="flex justify-between text-xs text-gray-500 mb-2">
                        <span>2024 ({t('scenarios.current')}: 37.2%)</span>
                        <span>2030 ({t('scenarios.projected')}: {scenario.penetrationBy2030}%)</span>
                      </div>

                      {/* Probability indicator */}
                      <div className="text-center mt-6">
                        <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${scenario.color} text-white`}>
                          <span className="font-semibold">{scenario.probability}% {t('scenarios.probabilityIndicator')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}
          </motion.div>
        )}

        {/* Call to Action */}
        <div className="mt-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-600"
          >
            <p className="mb-4">
              Which scenario do you think is most likely? Share your prediction and see how others vote.
            </p>
            <button className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-ai-600 to-primary-600 text-white rounded-lg hover:from-ai-700 hover:to-primary-700 transition-colors">
              Share Your Prediction
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

export default AIScenarios 